// =============================================================================
// DEAL CARD STYLES - Single Source of Truth for Card Styling
// =============================================================================
// CR043: Centralizes all deal card styling tokens, variants, and classes
// =============================================================================

import { cn } from "@/lib/utils";
import type { DealCategory } from "./dealTypeRegistry";

// =============================================================================
// CARD CONTAINER STYLES
// =============================================================================

export const cardStyles = {
  // Outer wrapper with hover effects
  wrapper: "group relative w-full max-w-[720px] mx-auto",
  
  // Main card container
  container: cn(
    "relative rounded-3xl overflow-hidden bg-white",
    "shadow-[0_10px_30px_rgba(0,0,0,0.08)]",
    "border border-black/5",
    "hover:shadow-[0_14px_38px_rgba(0,0,0,0.10)]",
    "transition-shadow duration-200"
  ),
  
  // Header band (top black band)
  headerBand: cn(
    "h-20 md:h-24",
    "bg-gradient-to-r from-black/90 to-black/70",
    "flex items-center px-6 pr-36 relative"
  ),
  
  // Body section
  body: "p-5 md:p-6 space-y-2",
  
  // Meta block (dates, source)
  metaBlock: "mt-3 space-y-1 text-sm text-black/70",
  
  // Actions row
  actionsRow: "mt-4 flex flex-wrap gap-2",
  
  // Footer row
  footer: "flex items-center justify-between pt-3 border-t border-black/5 mt-4",
} as const;

// =============================================================================
// HEADER BRAND STYLES
// =============================================================================

export const headerStyles = {
  logo: "h-10 md:h-12 object-contain max-w-[200px]",
  fallbackText: "text-white text-lg md:text-xl font-semibold truncate max-w-[200px]",
} as const;

// =============================================================================
// CORNER RIBBON STYLES
// =============================================================================

export const ribbonStyles = {
  // Base ribbon container
  container: "absolute top-0 right-0 w-36 h-36",
  
  // Ribbon clip path
  clipPath: "polygon(100% 0, 100% 100%, 0 0)" as const,
  
  // Content positioning
  content: "absolute top-4 right-4 text-right text-white",
  
  // Text styles
  label: "text-xs uppercase tracking-wide opacity-90 block",
  value: "text-lg font-bold block",
  subtext: "text-[10px] font-medium opacity-90 block truncate max-w-[70px]",
  
  // Merchant logo in ribbon
  merchantLogo: "h-4 w-4 object-contain",
  merchantFallback: "w-4 h-4 rounded-full bg-white/20 flex items-center justify-center",
  merchantName: "text-[10px] font-medium truncate max-w-[60px] opacity-90",
} as const;

// =============================================================================
// BADGE STYLES
// =============================================================================

export const badgeStyles = {
  // Base badge
  base: "inline-flex items-center font-medium rounded-full border",
  
  // Size variants
  sizes: {
    sm: "px-2 py-0.5 text-xs gap-1",
    md: "px-3 py-1 text-sm gap-1.5",
  },
  
  // Icon sizes
  iconSizes: {
    sm: "w-3 h-3",
    md: "w-4 h-4",
  },
  
  // Special badge variants
  membership: "bg-amber-100 text-amber-800 border-amber-200",
  corporate: "bg-purple-100 text-purple-800 border-purple-200",
  unclassified: "bg-slate-100 text-slate-800 border-slate-200",
  bulk: "bg-black/5 text-black/80",
  missingDetails: "bg-black/5 text-black/70",
  confirmOnSite: "bg-amber-100 text-amber-800",
} as const;

// =============================================================================
// BUTTON STYLES
// =============================================================================

export const buttonStyles = {
  // Primary action (View deal)
  primary: cn(
    "rounded-full px-4 py-2 text-sm font-medium",
    "bg-black text-white hover:bg-black/90",
    "transition-colors inline-flex items-center gap-1.5"
  ),
  
  // Secondary action (Source)
  secondary: cn(
    "rounded-full px-4 py-2 text-sm font-medium",
    "bg-black/5 text-black hover:bg-black/10",
    "transition-colors inline-flex items-center gap-1.5"
  ),
  
  // CTA action (Claim deal)
  cta: cn(
    "rounded-full px-4 py-2 text-sm font-medium",
    "bg-primary text-primary-foreground hover:bg-primary/90",
    "transition-colors inline-flex items-center gap-1.5"
  ),
} as const;

// =============================================================================
// STARBURST STYLES
// =============================================================================

export const starburstStyles = {
  // Position wrapper
  position: "absolute -top-5 -left-7 z-30",
  
  // Size variants
  sizes: {
    sm: "w-16 h-16",
    md: "w-24 h-24",
    lg: "w-28 h-28",
  },
  
  // Text sizes
  textSizes: {
    sm: { value: "text-sm", label: "text-[8px]" },
    md: { value: "text-xl", label: "text-[10px]" },
    lg: { value: "text-2xl", label: "text-xs" },
  },
} as const;

// =============================================================================
// CONFIDENCE DISPLAY STYLES
// =============================================================================

export const confidenceStyles = {
  High: {
    color: "text-success",
    bgColor: "bg-success/10",
  },
  Medium: {
    color: "text-accent",
    bgColor: "bg-accent/10",
  },
  Low: {
    color: "text-muted-foreground",
    bgColor: "bg-muted",
  },
} as const;

// =============================================================================
// DISCOUNT TIERS STYLES
// =============================================================================

export const tiersStyles = {
  container: "mt-2 space-y-1 text-sm text-black/70 bg-black/5 rounded-lg p-3",
  item: "",
  bulk: "text-xs font-medium text-black/60 mt-1",
} as const;

// =============================================================================
// ADMIN PANEL STYLES
// =============================================================================

export const adminPanelStyles = {
  trigger: cn(
    "flex items-center gap-1.5 text-xs text-muted-foreground",
    "hover:text-foreground transition-colors group w-full"
  ),
  content: "mt-2 space-y-2 text-xs bg-muted/50 rounded-lg p-3 border border-border",
  label: "font-semibold text-foreground",
  text: "text-muted-foreground mt-0.5",
} as const;

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get ribbon background class based on deal category and context
 */
export function getRibbonBgClass(
  category: DealCategory,
  isSameMerchant: boolean,
  isPartnerBonus: boolean,
  bonusMerchant?: string | null
): string {
  if (category === "POINTS") return "bg-purple-600";
  if (category === "DISCOUNT") return "bg-orange-500";
  if (category === "IN_KIND") return "bg-amber-600";
  
  // BONUS category with various contexts
  if (isPartnerBonus) return "bg-emerald-600";
  if (isSameMerchant) return "bg-green-600";
  
  // Different merchant bonus - check for specific merchants
  const isWalmartBonus = bonusMerchant?.toLowerCase().includes("walmart");
  if (isWalmartBonus) return "bg-[#0071CE]";
  
  return "bg-blue-600";
}

/**
 * Get badge size classes
 */
export function getBadgeSizeClasses(size: "sm" | "md" = "sm") {
  return {
    container: badgeStyles.sizes[size],
    icon: badgeStyles.iconSizes[size],
  };
}
